import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { buildGuardResponse, TogglePointDataType } from './toggle-point.guard';
import { verifyRequiredAttribute } from './toggle-point.directive';
import { DATA_ERROR, MISSING_PROPERTY_ERROR } from '../utils/constants';
import { FEATURE_TOGGLER, FeatureToggler } from '../feature-toggler';

export interface ExpectedParamType {
  parameter: string;
  expectedValue: string;
}

/**
 * Service that implements CanActivate which should be used to guard a route to
 * an experimental module or component.
 *
 * Some routes may have path parameters, which may generate a dynamic route. This
 * guard receives the parameter name and the expected value to prevent the user
 * to activate specific sections.
 *
 * Data should be provided on the route definition as well as a feature ID for
 * this guard to work.
 *
 * @example
 *
 * In this example, if the path looks as follows: `example/123/details`, the guard must prevent the
 * user to access it.
 *
 * ```typescript
 * ...
 * const routes: Routes = [
 *   {
 *     path: 'example/:recordId/details',
 *     component: MyComponent,
 *     canActivate: [TogglePointByParamGuard],
 *     data: {
 *       parameter: 'recordId',
 *       expectedValue: '123',
 *       featureId: 'example-id',
 *       // can have more data
 *     },
 *   },
 * ]
 * ...
 * ```
 */
@Injectable()
export class TogglePointByParamGuard {
  constructor(
    @Inject(FEATURE_TOGGLER) private featureToggler: FeatureToggler,
    private router: Router
  ) {}

  /**
   * Will check if the provided parameter exists on the route path and if its
   * value matches the expected value. If it does, a verification will be made
   * on the toggle router. This will determine if the route is enabled or not.
   *
   * @param data Must contain a parameter, expected value, the feature ID and
   *             could contain a redirection path.
   * @param paramMap Angular parameter map object.
   */
  canActivate({ data, paramMap }: ActivatedRouteSnapshot): boolean | UrlTree {
    verifyRequiredAttribute(data, DATA_ERROR);
    const { parameter, expectedValue, featureId, redirectPath } = data as ExpectedParamType & TogglePointDataType;
    verifyRequiredAttribute([parameter, expectedValue, featureId], MISSING_PROPERTY_ERROR);
    let isEnabled = true;

    if (paramMap.has(parameter) && paramMap.get(parameter) === expectedValue) {
      isEnabled = this.featureToggler.isFeatureEnabled(featureId);
    }
    return buildGuardResponse(isEnabled, redirectPath, this.router);
  }
}
